import { useEffect, useMemo, useState } from "react";
import { IconBolt, IconBuilding, IconEye, IconLock, IconRepeat } from "@tabler/icons-react";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import type { ReactNode } from "react";

import { contracts, getClientById } from "../../content";
import { useLocalize } from "../../content/useLocalize";
import { resolveBoard } from "../../engine/contractOffers";
import { filterContracts, groupByClient, type ContractFilterCriteria } from "../../engine/contractFilters";
import { DEFAULT_CLIENT_TRUST, REPLAY_REWARD_MULTIPLIER } from "../../engine/progression";
import { useGameStore } from "../../state/store";
import type { Client, ClientId, Contract, ContractId, LocalizedText } from "../../types";
import { Button } from "../common/Button";
import { ProgressBar } from "../common/ProgressBar";
import { cx } from "../common/cx";
import { ContractFilters } from "./ContractFilters";
import { MissionCard } from "./MissionCard";
import { useCountdown } from "./useCountdown";
import { urgencyTier } from "./urgency";
import styles from "./ContractBoard.module.css";

interface UrgentMissionCardProps {
  offer: { contract: Contract; deadlineMs: number; bonusPct: number; durationMs: number };
  client?: Client;
  trust: number;
  onOpen: () => void;
  onExpire: () => void;
}

function UrgentMissionCard({ offer, client, trust, onOpen, onExpire }: UrgentMissionCardProps): JSX.Element {
  const { label, remainingMs } = useCountdown(offer.deadlineMs, onExpire);
  const tier = urgencyTier(remainingMs, offer.durationMs);
  return (
    <div data-tutorial="recommended-contract">
      <MissionCard
        contract={offer.contract}
        client={client}
        trust={trust}
        urgent={{ label, bonusPct: offer.bonusPct, tier }}
        onOpen={onOpen}
      />
    </div>
  );
}

interface SectionHeaderProps {
  icon?: ReactNode;
  children: ReactNode;
}

function SectionHeader({ icon, children }: SectionHeaderProps): JSX.Element {
  return (
    <div className={styles.sectionLabel}>
      {icon && (
        <span className={styles.sectionIcon} aria-hidden="true">
          {icon}
        </span>
      )}
      <span>{children}</span>
    </div>
  );
}

function clientDomain(clientId: ClientId): string {
  // eslint-disable-next-line lingui/no-unlocalized-strings
  return `${clientId}.example`;
}

interface ClientGroupHeaderProps {
  name: string;
  domain: string;
  trust: number;
  count: number;
  trustLabel: string;
}

function ClientGroupHeader({ name, domain, trust, count, trustLabel }: ClientGroupHeaderProps): JSX.Element {
  return (
    <div className={styles.groupHead}>
      <span className={styles.groupIco} aria-hidden="true">
        <IconBuilding size={18} />
      </span>
      <div className={styles.gInfo}>
        <span className={styles.gName}>{name}</span>
        <span className={cx(styles.gDomain, "mono")}>{domain}</span>
      </div>
      <div className={styles.gTrust}>
        <span className={styles.gTrustLab}>
          <Trans>Доверие</Trans> <span className="mono">{trust}</span>
        </span>
        <ProgressBar value={trust} max={100} context="info" size="mini" label={trustLabel} />
      </div>
      <span className={cx(styles.gCount, "mono")}>{count}</span>
    </div>
  );
}

export function ContractBoard(): JSX.Element {
  const { i18n } = useLingui();
  const t = useLocalize();
  const player = useGameStore((state) => state.player);
  const offers = useGameStore((state) => state.offers);
  const clientTrust = useGameStore((state) => state.clientTrust);
  const resultHistory = useGameStore((state) => state.resultHistory);
  const setActiveContract = useGameStore((state) => state.setActiveContract);
  const navigateTo = useGameStore((state) => state.navigateTo);
  const refreshOffersNow = useGameStore((state) => state.refreshOffersNow);

  const [clientId, setClientId] = useState<string | "all">("all");
  const [difficulty, setDifficulty] = useState<number | "all">("all");
  const [search, setSearch] = useState("");
  const [availableOnly, setAvailableOnly] = useState(false);

  useEffect(() => {
    refreshOffersNow();
  }, [refreshOffersNow]);

  const nextUrgentAtMs = offers.nextUrgentAtMs ?? null;
  useEffect(() => {
    if (nextUrgentAtMs === null) {
      return;
    }
    const delay = nextUrgentAtMs - Date.now();
    if (delay <= 0) {
      refreshOffersNow();
      return;
    }
    const id = window.setTimeout(refreshOffersNow, delay);
    return () => window.clearTimeout(id);
  }, [nextUrgentAtMs, refreshOffersNow]);

  const board = useMemo(
    () => resolveBoard(offers, { contracts, player, now: Date.now(), rng: Math.random }),
    [offers, player],
  );

  const open = (id: ContractId): void => {
    setActiveContract(id);
    navigateTo("contract_details");
  };

  const trustOf = (id: ClientId): number =>
    clientTrust[id] ?? getClientById(id)?.trust ?? DEFAULT_CLIENT_TRUST;

  const localizeOrEmpty = (text: LocalizedText | undefined): string => (text ? t(text) : "");

  const criteria: ContractFilterCriteria = { clientId, difficulty, search };
  const titleOf = (c: Contract): string => t(c.title);

  const boardContracts = [...board.available, ...board.locked];
  const clientOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of boardContracts) {
      if (!seen.has(c.clientId)) {
        seen.set(c.clientId, localizeOrEmpty(getClientById(c.clientId)?.name));
      }
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [board]);
  const difficultyOptions = useMemo(
    () => [...new Set(boardContracts.map((c) => c.difficulty))].sort((a, b) => a - b),
    [board],
  );

  const completedContracts = useMemo(
    () => contracts.filter((c) => player.completedContracts.includes(c.id)),
    [player.completedContracts],
  );
  const lastGradeOf = (id: ContractId): string | undefined =>
    resultHistory.find((entry) => entry.contractId === id)?.grade;

  const filteredAvailable = filterContracts(board.available, criteria, titleOf);
  const filteredLocked = availableOnly ? [] : filterContracts(board.locked, criteria, titleOf);
  const filteredCompleted = availableOnly ? [] : filterContracts(completedContracts, criteria, titleOf);
  const groups = groupByClient(filteredAvailable);
  const resultCount = filteredAvailable.length + filteredLocked.length + filteredCompleted.length;

  const resetFilters = (): void => {
    setClientId("all");
    setDifficulty("all");
    setSearch("");
    setAvailableOnly(false);
  };

  return (
    <div className={styles.board}>
      {board.urgent && (
        <section className={styles.section}>
          <SectionHeader icon={<IconBolt size={16} />}>
            <Trans>Срочная миссия</Trans>
          </SectionHeader>
          <UrgentMissionCard
            offer={board.urgent}
            client={getClientById(board.urgent.contract.clientId)}
            trust={trustOf(board.urgent.contract.clientId)}
            onOpen={() => open(board.urgent!.contract.id)}
            onExpire={refreshOffersNow}
          />
        </section>
      )}

      <ContractFilters
        clientOptions={clientOptions}
        difficultyOptions={difficultyOptions}
        clientId={clientId}
        difficulty={difficulty}
        search={search}
        availableOnly={availableOnly}
        count={resultCount}
        onClient={setClientId}
        onDifficulty={setDifficulty}
        onSearch={setSearch}
        onAvailableOnly={setAvailableOnly}
        onReset={resetFilters}
      />

      {resultCount === 0 ? (
        <div className={styles.empty}>
          <Trans>Ничего не найдено.</Trans>{" "}
          <Button variant="ghost" size="small" onClick={resetFilters}>
            <Trans>Сбросить</Trans>
          </Button>
        </div>
      ) : (
        <>
          {groups.map((group, groupIndex) => {
            const client = getClientById(group.clientId);
            return (
              <section key={group.clientId} className={styles.section}>
                <ClientGroupHeader
                  name={localizeOrEmpty(client?.name)}
                  domain={clientDomain(group.clientId)}
                  trust={trustOf(group.clientId)}
                  count={group.items.length}
                  trustLabel={i18n._(msg`Доверие клиента`)}
                />
                <div className={styles.grid}>
                  {group.items.map((contract, index) => (
                    <div
                      key={contract.id}
                      data-tutorial={
                        !board.urgent && groupIndex === 0 && index === 0
                          ? "recommended-contract"
                          : undefined
                      }
                    >
                      <MissionCard
                        contract={contract}
                        client={client}
                        trust={trustOf(contract.clientId)}
                        onOpen={() => open(contract.id)}
                      />
                    </div>
                  ))}
                </div>
              </section>
            );
          })}

          {filteredCompleted.length > 0 && (
            <section className={styles.section}>
              <SectionHeader icon={<IconRepeat size={16} />}>
                <Trans>Завершённые</Trans>
              </SectionHeader>
              <ul className={styles.lockedList}>
                {filteredCompleted.map((contract) => {
                  const clientName = localizeOrEmpty(getClientById(contract.clientId)?.name);
                  const grade = lastGradeOf(contract.id);
                  return (
                    <li key={contract.id}>
                      <button type="button" className={styles.lockedRow} onClick={() => open(contract.id)}>
                        <span className={styles.lockedTitle}>
                          {t(contract.title)} · {clientName}
                        </span>
                        <span className={`${styles.lockedReq} mono`}>
                          {grade ? <Trans>оценка {grade}</Trans> : null}
                        </span>
                        <span className={styles.lockedPreview}>
                          <IconRepeat size={14} aria-hidden="true" />{" "}
                          <Trans>Пройти снова ×{REPLAY_REWARD_MULTIPLIER}</Trans>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {filteredLocked.length > 0 && (
            <section className={styles.section}>
              <SectionHeader icon={<IconLock size={16} />}>
                <Trans>Заблокированные</Trans>
              </SectionHeader>
              <ul className={styles.lockedList}>
                {filteredLocked.map((contract) => {
                  const requiredRep = contract.requiredReputation;
                  const reputation = player.reputation;
                  const clientName = localizeOrEmpty(getClientById(contract.clientId)?.name);
                  return (
                    <li key={contract.id}>
                      <button type="button" className={styles.lockedRow} onClick={() => open(contract.id)}>
                        <span className={styles.lockedTitle}>
                          {t(contract.title)} · {clientName}
                        </span>
                        <span className={`${styles.lockedReq} mono`}>
                          <Trans>
                            нужна репутация {requiredRep} · у тебя {reputation}
                          </Trans>
                        </span>
                        <span className={styles.lockedPreview}>
                          <IconEye size={14} aria-hidden="true" /> <Trans>Предпросмотр</Trans>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}
